import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
	Button,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';
import { useCreateEmployee } from '@/services/mutations/employee';
import { useGetCompanyDepartments } from '@/services/queries/company-department';

interface EmployeeModalProps {
	isOpen: boolean;
	onClose: () => void;
	companyId: string;
}

const formatCPFInput = (value: string) => {
	return value
		.replace(/\D/g, '')
		.replace(/(\d{3})(\d)/, '$1.$2')
		.replace(/(\d{3})(\d)/, '$1.$2')
		.replace(/(\d{3})(\d{1,2})$/, '$1-$2')
		.slice(0, 14);
};

export const EmployeeModal = ({
	isOpen,
	onClose,
	companyId,
}: EmployeeModalProps) => {
	const NONE_SECTOR = '__none__';
	const [cpf, setCpf] = useState('');
	const [error, setError] = useState('');
	const [departmentId, setDepartmentId] = useState('');
	const [sectorId, setSectorId] = useState(NONE_SECTOR);

	const { mutateAsync: createEmployee, isPending } = useCreateEmployee(() =>
		onClose(),
	);
	const { data: deptRes, isFetching: loadingDepts } =
		useGetCompanyDepartments(
			{ company_id: companyId, limit: 100 },
			{ enabled: isOpen && !!companyId },
		);

	const departments = deptRes?.data ?? [];
	const selectedDepartment = departments.find((d) => d.id === departmentId);
	const sectors = selectedDepartment?.company_sector ?? [];

	useEffect(() => {
		if (isOpen) {
			setCpf('');
			setError('');
			setDepartmentId('');
			setSectorId(NONE_SECTOR);
		}
	}, [isOpen]);

	const validate = () => {
		const clean = cpf.replace(/\D/g, '');
		if (!clean) {
			setError('CPF é obrigatório');
			return false;
		}
		if (clean.length !== 11) {
			setError('CPF deve ter 11 dígitos');
			return false;
		}
		setError('');
		return true;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!validate()) return;
		const body: Employees.Create = {
			company_id: companyId,
			cpf: cpf.replace(/\D/g, ''),
		};
		if (departmentId) {
			body.department_id = departmentId;
		}
		if (departmentId && sectorId !== NONE_SECTOR) {
			body.sector_id = sectorId;
		}
		await createEmployee(body);
	};

	if (!isOpen) return null;

	const inputClass = cn(
		'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm font-bold text-stone-600 outline-none transition-all box-border',
		error
			? 'border-rose-500 bg-rose-50/30 focus:border-rose-600'
			: 'border-stone-200 focus:border-stone-400 focus:bg-white',
	);

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<form
					onSubmit={handleSubmit}
					noValidate
					className="flex flex-col overflow-hidden"
				>
					<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-8 py-6">
						<h2 className="text-lg font-black text-stone-900">
							Novo colaborador
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="text-stone-400 transition-colors hover:text-stone-600"
						>
							<X size={20} />
						</button>
					</div>

					<div className="flex-1 space-y-6 overflow-y-auto p-8">
						<p className="text-sm text-stone-500">
							Cadastre pelo CPF. Se existir um usuário com esse
							CPF, o vínculo será feito automaticamente e nome e
							e-mail virão do cadastro do usuário.
						</p>
						<div>
							<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-300">
								CPF
							</label>
							<input
								type="text"
								value={cpf}
								onChange={(e) =>
									setCpf(formatCPFInput(e.target.value))
								}
								className={inputClass}
								placeholder="000.000.000-00"
							/>
							{error && (
								<span className="animate-in fade-in slide-in-from-top-1 ml-1 mt-1 flex items-center gap-1 text-[10px] font-bold text-rose-500">
									<AlertCircle size={10} /> {error}
								</span>
							)}
						</div>

						<div>
							<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-300">
								Departamento (opcional)
							</label>
							<Select
								value={departmentId}
								onValueChange={(value) => {
									setDepartmentId(value);
									setSectorId(NONE_SECTOR);
								}}
								disabled={
									loadingDepts || departments.length === 0
								}
							>
								<SelectTrigger
									id="create-employee-dept"
									className={inputClass}
								>
									<SelectValue>
										{loadingDepts
											? 'Carregando...'
											: departmentId
											  ? departments.find(
														(d) =>
															d.id ===
															departmentId,
											    )?.name ?? ''
											  : departments.length === 0
											    ? 'Nenhum departamento vinculado'
											    : 'Selecione o departamento'}
									</SelectValue>
								</SelectTrigger>
								<SelectContent dropdownWidth="max-content">
									{departments.map((department) => (
										<SelectItem
											key={department.id}
											value={department.id}
											noTruncate
										>
											{department.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div>
							<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-300">
								Setor (opcional)
							</label>
							<Select
								value={sectorId}
								onValueChange={setSectorId}
								disabled={!departmentId}
							>
								<SelectTrigger
									id="create-employee-sector"
									className={inputClass}
								>
									<SelectValue>
										{!departmentId
											? 'Selecione o departamento primeiro'
											: sectorId === NONE_SECTOR
											  ? 'Sem setor específico'
											  : sectors.find(
														(s) =>
															s.id === sectorId,
											    )?.name ??
											    'Sem setor específico'}
									</SelectValue>
								</SelectTrigger>
								<SelectContent dropdownWidth="max-content">
									<SelectItem value={NONE_SECTOR} noTruncate>
										Sem setor específico
									</SelectItem>
									{sectors.map((sector) => (
										<SelectItem
											key={sector.id}
											value={sector.id}
											noTruncate
										>
											{sector.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>

					<div className="flex shrink-0 items-center justify-end gap-6 border-t border-stone-50 p-8">
						<button
							type="button"
							onClick={onClose}
							disabled={isPending}
							className="text-sm font-bold text-stone-400 hover:text-stone-600 disabled:opacity-50"
						>
							Cancelar
						</button>
						<Button
							type="submit"
							text={
								isPending ? 'Salvando...' : 'Criar colaborador'
							}
							disabled={isPending}
							className="rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800"
							startIcon={
								isPending ? (
									<Loader2 className="size-4 animate-spin" />
								) : (
									<Check className="size-4 stroke-[3]" />
								)
							}
						/>
					</div>
				</form>
			</div>
		</div>,
		document.body,
	);
};
